import { mountTable } from './Table';
import {
    Accordion,
    ACCORDION_ITEM_CONTENT_INNER_CLASSNAME,
    IAccordionItemProperties,
} from '@vfde-brix/ws10/accordion';
import mountAccordion from './Accordion';
import { TECHNICAL_DETAILS_ACCORDION_CONTAINER_ID } from 'Container/App/constants';
import { StateProps } from 'Container/App/interface';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-sails/constants';
import {
    createId,
    decodeHtml,
} from '@vfde-sails/utils';
import { NO_PATTERN_BUSINESS_LOGIC } from '@vfde-brix/ws10/core';
import {
    selectActiveAccordionPanel,
    toggleAccordionItem,
} from 'Container/App/slice';
import { startAppListening } from 'Container/App/listener';
import {
    selectDeliveryScope,
    selectTechnicalDetails,
} from 'Container/App/selectors';
import { useDispatch } from '../store';
import { ACCORDION_ITEM_CONTENT_ID_PREFIX } from '@vfde-brix/ws10/accordion/Constants';

let cmsItemIds: string[];

const prepareTechnicalDetailsAccordion = () => {
    startAppListening({
        predicate: (action, currentState, previousState) =>
            selectTechnicalDetails(currentState) !== selectTechnicalDetails(previousState),
        effect: (action, listenerApi) => {
            listenerApi.cancelActiveListeners();

            const previousTechnicalDetails = selectTechnicalDetails(listenerApi.getOriginalState()).length;

            const technicalDetails = selectTechnicalDetails(listenerApi.getState());
            const deliveryScope = selectDeliveryScope(listenerApi.getState());
            const activeAccordionPanel = selectActiveAccordionPanel(listenerApi.getState());

            mountTechnicalDetailsAccordion({
                technicalDetails,
                deliveryScope,
                activeAccordionPanel,
            }, !previousTechnicalDetails);
        },
    });
};

export default prepareTechnicalDetailsAccordion;

/**
 * Mounts the technical-details accordion
 */
export const mountTechnicalDetailsAccordion = (
    state: Pick<StateProps, 'technicalDetails' | 'deliveryScope' | 'activeAccordionPanel'>,
    isFirstCall: boolean,
): Accordion | null => {
    const container = document.getElementById(TECHNICAL_DETAILS_ACCORDION_CONTAINER_ID);

    if (!container) {
        /* istanbul ignore next */
        return null;
    }

    const dispatch = useDispatch();

    const tmpContainer = document.createElement('div');

    const accordion = mountAccordion(container, {
        onToggle: (itemProperties: IAccordionItemProperties) => {
            dispatch(toggleAccordionItem({
                stdHeadline: itemProperties.stdHeadline,
                optOpen: itemProperties.optOpen,
                stdId: itemProperties.stdId,
            }));
        },
    });
    const items: IAccordionItemProperties[] = [
        ...createTechnicalDetailsData(state.technicalDetails, state.activeAccordionPanel),
        createDeliveryScopeData(state.deliveryScope!),
    ];
    const tmpAccordion = mountAccordion(tmpContainer, { items, business: NO_PATTERN_BUSINESS_LOGIC });
    const cmsItems = getItemsFromCMS(accordion, isFirstCall);

    if (isFirstCall) {
        cmsItemIds = cmsItems.map(item => item.stdId);
    }

    accordion.update({
        items: [
            ...cmsItems,
            ...tmpAccordion.getPropertyByKey('items'),
        ],
        // Has to be false, because accordion is generated on the fly and deeplink on reload doesn't work with dynamic accordions
        optDeepLink: false,
        optOpenIndividually: true,
    });

    return accordion;
};

/**
 * Reads the editorial item properties from the given accordion
 * and returns them as array. When this function is called
 * the first time `isFirstCall` must be set to true, so that
 * all items can be returned. On any following call `isFirstCall`
 * should be false so that the items will be compared to the
 * `cmsItemIds` array which contains the IDs of accordion items
 * coming from the CMS.
 * @param accordion The accordion component object
 * @param isFirstCall Boolean
 * @returns Array of accordion item properties
 */
const getItemsFromCMS = (accordion: Accordion, isFirstCall: boolean): IAccordionItemProperties[] => {
    const cmsItems: IAccordionItemProperties[] = accordion.getPropertyByKey('items');
    const items: IAccordionItemProperties[] = [];

    for (const cmsItem of cmsItems) {
        const isWhitelisted = isFirstCall || cmsItemIds.indexOf(cmsItem.stdId) !== -1;

        if (isWhitelisted) {
            const wrapper = document.getElementById(`${ACCORDION_ITEM_CONTENT_ID_PREFIX}${cmsItem.stdId}`);
            cmsItem.containerAnyComponent = wrapper?.getElementsByClassName(ACCORDION_ITEM_CONTENT_INNER_CLASSNAME)[0].innerHTML;

            // TODO: PatternLib: containerAnyComponent needs to be read in readDom

            if (cmsItem.containerAnyComponent?.trim().length > 0) {
                items.push(cmsItem);
            }
        }
    }

    return items;
};

const createTechnicalDetailsData = (
    technicalDetails: StateProps['technicalDetails'],
    activePanel: StateProps['activeAccordionPanel'],
): IAccordionItemProperties[] => {
    const accordionItems: IAccordionItemProperties[] = [];
    let i = 0;

    for (const section of technicalDetails) {
        const tableContainer = document.createElement('div');

        mountTable(tableContainer, section.attributes);

        const stdId = createId(decodeHtml(section.label), undefined, i.toString());
        const stdIconLeft = window[ADDITIONAL_PAGE_OPTIONS].technicalDetails.icons[section.id];
        const optOpen = activePanel === stdId;

        accordionItems.push({
            stdId,
            stdHeadline: section.label,
            containerAnyComponent: tableContainer.innerHTML,
            stdIconLeft,
            optOpen,
        });

        i++;
    }

    return accordionItems;
};

const createDeliveryScopeData = (deliveryScope: string[]): IAccordionItemProperties => {
    const deliveryScopeElement = document.createElement('div');

    deliveryScopeElement.innerHTML = `<ul class="ws10-bullet-list">${deliveryScope.map(deliveryScopeItem => `<li>${deliveryScopeItem}</li>`).join('')}</ul>`;

    return {
        stdId: createId(window[ADDITIONAL_PAGE_OPTIONS].technicalDetails.headline, 'ddp-delivery-scope'),
        stdHeadline: window[ADDITIONAL_PAGE_OPTIONS].technicalDetails.headline,
        containerAnyComponent: deliveryScopeElement.innerHTML,
        stdIconLeft: 'bundles',
    };
};
